import { API_V2_BASE_URL } from "@config";
import { getV2AccessToken, refreshV2, clearV2Session } from "../auth/v2Auth";

export class V2ApiError extends Error {
  constructor(status, message, body = null) {
    super(message);
    this.name = "V2ApiError";
    this.status = status;
    this.body = body;
    this.code = body?.error?.code;
  }
}

function errorMessage(data, fallback) {
  if (typeof data?.error === "string") return data.error;
  return data?.error?.message || fallback;
}

async function parseJson(response) {
  const text = await response.text();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    return {};
  }
}

async function rawRequest(path, { method = "GET", headers, body, signal } = {}, token) {
  const response = await fetch(`${API_V2_BASE_URL}${path}`, {
    method,
    signal,
    headers: {
      Accept: "application/json",
      ...(body ? { "Content-Type": "application/json" } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(headers || {}),
    },
    body: body == null ? undefined : JSON.stringify(body),
  });
  const data = await parseJson(response);
  if (!response.ok) {
    throw new V2ApiError(response.status, errorMessage(data, `Request failed (${response.status})`), data);
  }
  return data;
}

async function withAuthRetry(run, options = {}) {
  const token = getV2AccessToken();
  if (!token) {
    throw new V2ApiError(401, "Bearer token required");
  }

  try {
    return await run(token);
  } catch (error) {
    if (error.status !== 401 || options._retried) {
      throw error;
    }
    const refreshed = await refreshV2();
    if (!refreshed?.access_token) {
      clearV2Session();
      throw error;
    }
    return run(refreshed.access_token);
  }
}

export async function v2Request(path, options = {}) {
  return withAuthRetry((token) => rawRequest(path, options, token), options);
}

async function rawBlobRequest(path, { signal, headers } = {}, token) {
  const response = await fetch(`${API_V2_BASE_URL}${path}`, {
    method: "GET",
    signal,
    headers: {
      Accept: "image/*",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(headers || {}),
    },
  });
  if (response.status === 404) return null;
  if (!response.ok) {
    const data = await parseJson(response);
    throw new V2ApiError(response.status, errorMessage(data, `Request failed (${response.status})`), data);
  }
  return response.blob();
}

export async function v2RequestBlob(path, options = {}) {
  return withAuthRetry((token) => rawBlobRequest(path, options, token), options);
}
